import {
  getAllConversations as getAllConversationsAPI,
  getFilteredConversations as getFilteredConversationsAPI,
  getUnreadConversationsCount as getUnreadConversationsCountAPI,
  type FilteredConversationsParams,
} from "@/api/conversation";
import httpRequest from "@/auth/http-client";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import { IChat } from "@/interfaces/chat.interfaces";
import { IConversationTransferStep } from "@/interfaces/conversation-transfer.interfaces";
import { IConversation, IConversationMessages } from "@/interfaces/conversation.interfaces";
import { IMessage } from "@/interfaces/message.interface";
import {
  ConversationDetailsByContactIdParams,
  ConversationDetailsParams,
  ConversationsParams,
} from "@/types/message.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

class ConversationService {
  private baseUrl = "api/v1/conversation";
  async getAllConversations(params: ConversationsParams): Promise<IChat[]> {
    const { platformId } = params;

    try {
      // Use the new API that handles internal authentication
      const conversations = await getAllConversationsAPI(platformId);
      return conversations;
    } catch (error) {
      // Return empty array if there's an error fetching conversations
      return [];
    }
  }

  /**
   * Lista conversas da aba/filtro selecionados, uma página por vez (lazy load).
   * Diferente de getAllConversations, os filtros são aplicados na query do banco.
   */
  async getFilteredConversations(
    params: FilteredConversationsParams
  ): Promise<PaginatedResponse<IChat>> {
    return await getFilteredConversationsAPI(params);
  }

  async getUnreadConversationsCount(platformId: number): Promise<number> {
    return await getUnreadConversationsCountAPI(platformId);
  }

  async getConversation(conversationId: number): Promise<IConversation> {
    const conversation = await httpRequest.get<IConversation>(`${this.baseUrl}/${conversationId}`);

    return conversation;
  }

  async takeConversation(conversationId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${conversationId}/take`);
  }

  async finishConversation(conversationId: number): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${conversationId}/finish`);
  }

  async getTransferSteps(): Promise<IConversationTransferStep[]> {
    const steps = await httpRequest.get<IConversationTransferStep[]>(
      `${this.baseUrl}/transfer-steps`
    );
    return steps || [];
  }

  async gotoConversationStep(
    conversationId: number,
    payload: { stepName: string; workflowId: number }
  ): Promise<void> {
    await httpRequest.put(`${this.baseUrl}/${conversationId}/goto-step`, payload);
  }

  async getConversationMessages(conversationId: number): Promise<IConversationMessages> {
    const conversationMessages = await httpRequest.get(`${this.baseUrl}/detail/${conversationId}`);

    return conversationMessages;
  }

  async getConversationDetails(
    params: ConversationDetailsByContactIdParams,
    pagination: RequestPagination
  ): Promise<PaginatedResponse<IMessage>> {
    const { platformId, contactId } = params;
    const paginationQuery = buildPaginationQuery(pagination);

    const conversationDetails = await httpRequest.get(
      `${this.baseUrl}/detail/${platformId}/${contactId}?${paginationQuery}`
    );
    return conversationDetails;
  }

  async markAllAsRead({ platformId, conversationId }: ConversationDetailsParams) {
    await httpRequest.put(`${this.baseUrl}/detailAsRead/${platformId}/${conversationId}`);
  }

  async startConversation(contactId: number, platformId: number): Promise<void> {
    await httpRequest.post(`${this.baseUrl}/${contactId}/start`, {
      platformId,
    });
  }
}

const conversationService = new ConversationService();
export default conversationService;
